import { useState, useEffect, useRef } from "react";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CloseEyeIcon } from "@/components/icons";
import { EyeIcon } from "@/components/icons/eye-icon";
import {
  CityConfigFormValues,
  EASYPARK_CODE_HELP,
  TICKET_PROVIDER_OPTIONS,
  easyparkAreaSchema,
  validityAreaSchema,
} from "../../parking-payment.const";
import { TicketProviderEnum } from "../../payment-zone-area.type";
import { EditableStringList } from "./editable-string-list";
import { NamedNumberListEditor } from "./named-number-list-editor";
import { SubSectionLabel } from "./sub-section-label";
import * as z from "zod";

type ValidityArea = z.infer<typeof validityAreaSchema>;
type EasyparkAreaValue = z.infer<typeof easyparkAreaSchema>;

type TicketProviderSectionProps = {
  control: Control<CityConfigFormValues>;
  setValue: UseFormSetValue<CityConfigFormValues>;
};

export const TicketProviderSection = ({ control, setValue }: TicketProviderSectionProps) => {
  const [showPassword, setShowPassword] = useState(false);

  const ticketProviderType = useWatch({ control, name: "ticketProvider.provider" });
  const passwordAlreadySet = useWatch({ control, name: "ticketProvider.passwordAlreadySet" });
  const extensoProfiles = useWatch({ control, name: "ticketProvider.extensoProfiles" });
  const prevType = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (prevType.current !== undefined && prevType.current !== ticketProviderType) {
      setValue("ticketProvider.code", "");
      setValue("ticketProvider.easyparkAreas", []);
      setValue("ticketProvider.easyparkAreaCountryCode", "");
      setValue("ticketProvider.extensoETicketUrl", undefined);
      setValue("ticketProvider.extensoETicketPassword", "");
      setValue("ticketProvider.passwordAlreadySet", false);
      setValue("ticketProvider.extensoValidityAreas", []);
      setValue("ticketProvider.extensoProfiles", []);
      setValue("ticketProvider.extensoDefaultProfile", undefined);
      setValue("ticketProvider.polyconseilZones", []);
      setValue("ticketProvider.polyconseilSubscriptionPlans", []);
      setValue("ticketProvider.polyconseilPricingCategories", []);
      setValue("ticketProvider.polyconseilTariffCodes", []);
      setValue("ticketProvider.polyconseilValidityAreas", []);
      setValue("ticketProvider.prestoPlatformPointsOfSale", []);
      setShowPassword(false);
    }
    prevType.current = ticketProviderType;
  }, [ticketProviderType, setValue]);

  const isEasypark = ticketProviderType === TicketProviderEnum.Easypark;
  const isExtenso = ticketProviderType === TicketProviderEnum.Extenso;
  const isExtensoE2 = ticketProviderType === TicketProviderEnum.ExtensoE2;
  const isPolyconseil = ticketProviderType === TicketProviderEnum.Polyconseil;
  const isPrestoPlatform = ticketProviderType === TicketProviderEnum.PrestoPlatform;

  return (
    <div className="space-y-4">
      <FormField
        control={control}
        name="ticketProvider.provider"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Type de provider</FormLabel>
            <Select onValueChange={(v) => field.onChange(v === "__none__" ? undefined : v)} value={field.value ?? ""}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez un provider" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="__none__">Aucun</SelectItem>
                {TICKET_PROVIDER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {ticketProviderType && (
        <>
          <FormField
            control={control}
            name="ticketProvider.code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Code <span className="text-red-500">*</span>
                </FormLabel>
                {isEasypark && <SubSectionLabel label={EASYPARK_CODE_HELP} />}
                <FormControl>
                  <Input placeholder={isEasypark ? "Ex. lisbon" : "Ex. BAG0028"} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* easypark */}
          {isEasypark && (
            <>
              <FormField
                control={control}
                name="ticketProvider.easyparkAreas"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Zones EasyPark</FormLabel>
                    <SubSectionLabel label="Nom + numéro de zone fourni par EasyPark. Les tarifs de la ville sont rattachés à ces zones." />
                    <FormControl>
                      <NamedNumberListEditor
                        value={((value as EasyparkAreaValue[]) ?? []).map(({ name, areaNo }) => ({
                          name,
                          number: areaNo,
                        }))}
                        onChange={(items) => onChange(items.map(({ name, number }) => ({ name, areaNo: number })))}
                        withNumber
                        numberPlaceholder="N° de zone"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="ticketProvider.easyparkAreaCountryCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Code pays <span className="text-red-500">*</span>
                    </FormLabel>
                    <SubSectionLabel label="Code pays ISO 3166-1 alpha-2, en majuscules." />
                    <FormControl>
                      <Input
                        placeholder="Ex. PT"
                        maxLength={2}
                        {...field}
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}

          {/* extenso only */}
          {isExtenso && (
            <>
              <FormField
                control={control}
                name="ticketProvider.extensoETicketUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      URL eTicket <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="https://..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="ticketProvider.extensoETicketPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Mot de passe eTicket {!passwordAlreadySet && <span className="text-red-500">*</span>}
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder={passwordAlreadySet ? "•••• déjà défini" : "••••••••"}
                          className="pr-10"
                          {...field}
                        />
                        <button
                          type="button"
                          aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                          onClick={() => setShowPassword((v) => !v)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <CloseEyeIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                        </button>
                      </div>
                    </FormControl>
                    {passwordAlreadySet && (
                      <SubSectionLabel label="Un mot de passe est déjà enregistré. Laissez ce champ vide pour le conserver." />
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}

          {/* extenso + extenso_e2 */}
          {(isExtenso || isExtensoE2) && (
            <>
              <FormField
                control={control}
                name="ticketProvider.extensoValidityAreas"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Zones de validité</FormLabel>
                    <SubSectionLabel label={isExtenso ? "Nom + ID eTicket" : "Nom uniquement"} />
                    <FormControl>
                      <NamedNumberListEditor
                        value={((value as ValidityArea[]) ?? []).map(({ name, eTicketId }) => ({
                          name,
                          number: eTicketId,
                        }))}
                        onChange={(items) =>
                          onChange(
                            items.map(({ name, number }) => (isExtenso ? { name, eTicketId: number } : { name })),
                          )
                        }
                        withNumber={isExtenso}
                        numberPlaceholder="ID eTicket"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="ticketProvider.extensoProfiles"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Profils</FormLabel>
                    <FormControl>
                      <EditableStringList
                        value={(value as string[]) ?? []}
                        onChange={onChange}
                        placeholder="Ex. resident"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="ticketProvider.extensoDefaultProfile"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Profil par défaut</FormLabel>
                    <Select
                      onValueChange={(v) => field.onChange(v === "__none__" ? undefined : v)}
                      value={field.value ?? ""}
                      disabled={!(extensoProfiles ?? []).length}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez un profil par défaut" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">Aucun</SelectItem>
                        {(extensoProfiles ?? []).map((profile) => (
                          <SelectItem key={profile} value={profile}>
                            {profile}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}

          {/* polyconseil */}
          {isPolyconseil && (
            <>
              <FormField
                control={control}
                name="ticketProvider.polyconseilZones"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Zones</FormLabel>
                    <FormControl>
                      <EditableStringList
                        value={(value as string[]) ?? []}
                        onChange={onChange}
                        placeholder="Ex. ZoneA"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="ticketProvider.polyconseilSubscriptionPlans"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Plans d'abonnement</FormLabel>
                    <FormControl>
                      <EditableStringList
                        value={(value as string[]) ?? []}
                        onChange={onChange}
                        placeholder="Ex. mensuel"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="ticketProvider.polyconseilPricingCategories"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Catégories tarifaires</FormLabel>
                    <FormControl>
                      <EditableStringList
                        value={(value as string[]) ?? []}
                        onChange={onChange}
                        placeholder="Ex. standard"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="ticketProvider.polyconseilTariffCodes"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Codes tarifaires</FormLabel>
                    <FormControl>
                      <EditableStringList value={(value as string[]) ?? []} onChange={onChange} placeholder="Ex. T01" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="ticketProvider.polyconseilValidityAreas"
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormLabel>Zones de validité</FormLabel>
                    <FormControl>
                      <EditableStringList
                        value={(value as string[]) ?? []}
                        onChange={onChange}
                        placeholder="Ex. VA_centre"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}

          {/* presto_platform */}
          {isPrestoPlatform && (
            <FormField
              control={control}
              name="ticketProvider.prestoPlatformPointsOfSale"
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormLabel>Points de vente</FormLabel>
                  <FormControl>
                    <EditableStringList
                      value={(value as string[]) ?? []}
                      onChange={onChange}
                      placeholder="Ex. POS-01"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </>
      )}
    </div>
  );
};
